import { SASortObject } from 'types/SearchProvider'
import { SORT_BY_AND_ORDER, SORT_VALUES } from 'lib/constructor/constants/hardcodedSaatchiFilters'
import ISO2_GEO_ZONES from 'lib/constants/iso2GeoZones'
import { slugify } from 'lib/slugify'
import { GetSortValuesParams, SortObject } from '../types'

const CURATED_STATUS_MAPPING_VALUES = {
  ['down']: -1,
  ['not-curated']: 0,
  ['up']: 1,
}

type RulesToSendFiltersParams = {
  filters: Record<string, ReadonlyArray<string>>
  destinationZone?: string
  userSizePreference?: string
  isPrintsPage?: boolean
  shouldAddGroupIdFilter?: boolean
}

export const handleScrollToTop = async (callbackFunction?: () => void): Promise<void> => {
  if (window !== undefined) {
    const scrollToTopAndThen = (callback?: () => void) => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      // Use requestAnimationFrame to check if the page has finished scrolling
      const checkScroll = () => {
        // If the scroll position is at the top, execute the next callback
        if (window.scrollY === 0) {
          // Stop the requestAnimationFrame loop and perform the next callback
          callback?.()
        } else {
          // Otherwise, keep checking
          requestAnimationFrame(checkScroll)
        }
      }
      // Start checking if the scroll is complete
      requestAnimationFrame(checkScroll)
    }

    scrollToTopAndThen(callbackFunction)
  }
}

const convertInchesToCentimeters = (inchesValue: string) => {
  const multiplierInchesToCentimeters = 2.54
  return parseFloat(`${Number(inchesValue) * multiplierInchesToCentimeters}`).toFixed(0)
}

export const rulesToSendFilters = ({
  filters,
  destinationZone = 'US',
  isPrintsPage,
  userSizePreference,
  shouldAddGroupIdFilter,
}: RulesToSendFiltersParams) => {
  // Remove basic query params from selectedFacets
  const basicQueryParam = ['query', 'hitsPerPage', 'page', 'sort']
  const filtersToRemove = [
    'has_prints',
    'category',
    'custom__geo_prices',
    'custom__print_all_prices',
    'curation_portal_query',
    'originals_prints',
  ]
  const mergedFiltersToRemove = [...basicQueryParam, ...filtersToRemove]
  const filtersWithoutBasicQueryParams = Object.fromEntries(
    Object.entries(filters).filter(([key]) => !mergedFiltersToRemove.includes(key))
  )

  // Format geo_prices
  if (filtersWithoutBasicQueryParams?.['geo_prices']?.length) {
    const ranges = filtersWithoutBasicQueryParams['geo_prices'].map((range) => {
      const [min, max] = range.split('-').map(Number)
      const minPriceInCents = min * 100
      const maxPriceInCents = max * 100
      return `${minPriceInCents}-${maxPriceInCents}`
    })

    filtersWithoutBasicQueryParams[destinationZone] = ranges
    delete filtersWithoutBasicQueryParams['geo_prices']
  }

  // Format print_all_prices
  if (filtersWithoutBasicQueryParams?.['print_all_prices_values']?.length) {
    const ranges = filtersWithoutBasicQueryParams['print_all_prices_values'].map((range) => {
      const [min, max] = range.split('-').map(Number)
      const minPriceInCents = min * 100
      const maxPriceInCents = max * 100
      return `${minPriceInCents}-${maxPriceInCents}`
    })

    filtersWithoutBasicQueryParams['print_all_prices'] = ranges
    delete filtersWithoutBasicQueryParams['print_all_prices_values']
  }

  // Adding category filter to prints groups request (as we remove category from filters we may receive the correct group_id before final filters)
  if (shouldAddGroupIdFilter) {
    const groupIdArray = filtersWithoutBasicQueryParams['group_id'] || filters['category']
    const slugifiedGroupIdArray: ReadonlyArray<string> = groupIdArray.map(slugify)
    if (slugifiedGroupIdArray.length > 0) {
      filtersWithoutBasicQueryParams['group_id'] = slugifiedGroupIdArray
    }
  }

  // Formating boolean facets from 'true' to 'True' as expected by Constructor
  for (const key in filtersWithoutBasicQueryParams) {
    if (Array.isArray(filtersWithoutBasicQueryParams[key])) {
      filtersWithoutBasicQueryParams[key] = filtersWithoutBasicQueryParams[key].map((filterValue) =>
        filterValue === 'true' ? 'True' : filterValue
      )
    }
  }

  // Format Custom dimensions to always send centimeters
  if (Boolean(filtersWithoutBasicQueryParams?.['width'])) {
    if (userSizePreference === 'in') {
      const [widthMin, widthMax] = (filtersWithoutBasicQueryParams['width']?.[0] || '').split('-')
      filtersWithoutBasicQueryParams['width'] = [
        `${convertInchesToCentimeters(widthMin)}-${convertInchesToCentimeters(widthMax)}`,
      ]
    }
  }
  if (Boolean(filtersWithoutBasicQueryParams?.['height'])) {
    if (userSizePreference === 'in') {
      const [heightMin, heightMax] = (filtersWithoutBasicQueryParams['height']?.[0] || '').split(
        '-'
      )
      filtersWithoutBasicQueryParams['height'] = [
        `${convertInchesToCentimeters(heightMin)}-${convertInchesToCentimeters(heightMax)}`,
      ]
    }
  }

  // Format curated_status to map values like -1 that in slugify can't be in URL
  if (Boolean(filtersWithoutBasicQueryParams?.['curated_status_value'])) {
    const curatedStatus = filtersWithoutBasicQueryParams['curated_status_value'].map(
      (curatedValue) => CURATED_STATUS_MAPPING_VALUES[curatedValue]
    )
    filtersWithoutBasicQueryParams['curated_status'] = curatedStatus
    delete filtersWithoutBasicQueryParams['curated_status_value']
  }

  return filtersWithoutBasicQueryParams
}

export const getSortValues = ({ sort, destinationZone = '' }: GetSortValuesParams): SortObject => {
  const isSortByPrice = [SORT_VALUES.PRICE_LOW_TO_HIGH, SORT_VALUES.PRICE_HIGH_TO_LOW].includes(
    sort
  )
  const sortObject = {
    ...(SORT_BY_AND_ORDER[sort] || { sortBy: '', sortOrder: '' }),
  }

  // Needed to include destination zone in the sorting by price
  if (isSortByPrice) {
    sortObject.sortBy = `${SORT_BY_AND_ORDER[sort].sortBy}-${destinationZone}`
  }

  return sortObject
}

const removeDestinationZoneSuffix = (str: string, destinationZone: string) => {
  const uniqueDestinationZones = Array.from(new Set(Object.values(ISO2_GEO_ZONES)))

  if (Boolean(str) && uniqueDestinationZones.includes(destinationZone)) {
    const destinationZoneToRemove = new RegExp(`-${destinationZone}$`)
    return str.replace(destinationZoneToRemove, '')
  }

  return str
}

export const getSortKeyByValue = (
  value: SASortObject,
  destinationZone: string
): string | undefined => {
  if (!value) {
    return undefined
  }

  const sortByWithoutDestinationZone = removeDestinationZoneSuffix(value.sortBy, destinationZone)

  return (Object.entries(SORT_BY_AND_ORDER).find(([_, sortConfig]) => {
    return (
      sortConfig?.sortBy === sortByWithoutDestinationZone &&
      sortConfig?.sortOrder === value.sortOrder
    )
  }) || [])[0]
}
